/**
 * Meta (Facebook) Pixel — chargé seulement après consentement et hors pages auth publiques.
 * Désactiver : VUE_APP_META_PIXEL_ENABLED=false
 * ID pixel : VUE_APP_META_PIXEL_ID (défaut aligné sur le pixel Studio si non défini).
 */
import Cookies from 'js-cookie';
import { isLightBootPublicPath } from '../utils/marketingPages.js';

const ENABLED =
  String(process.env.VUE_APP_META_PIXEL_ENABLED ?? 'true').toLowerCase() !== 'false';

const PIXEL_ID = String(
  process.env.VUE_APP_META_PIXEL_ID || '1513018317068320'
).trim();

let initDone = false;
let queuedPageViewPath = null;
let fbqBootstrapPromise = null;

function deferMetaPixelWork(task) {
  if (typeof window === 'undefined') return;
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(task, { timeout: 2500 });
    return;
  }
  window.setTimeout(task, 150);
}

function getConsentCookieRaw() {
  if (typeof document === 'undefined') return null;
  let value = Cookies.get('consent');
  if (value !== undefined && value !== null && value !== '') return String(value);
  const match = document.cookie.match(/(?:^|;\s*)consent=([^;]*)/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1].trim());
  } catch (_err) {
    return match[1];
  }
}

function isConsentGranted() {
  const consent = getConsentCookieRaw();
  if (consent == null || consent === '') return false;
  const normalized = String(consent).trim().toLowerCase();
  return (
    normalized === '1' ||
    normalized === 'true' ||
    normalized === 'yes' ||
    normalized === 'on'
  );
}

function normalizePath(path = '') {
  const source =
    path ||
    (typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : '/');
  try {
    return new URL(String(source), 'https://studio.mydid.local').pathname;
  } catch (_err) {
    return String(source || '/').split('?')[0] || '/';
  }
}

function isPublicLightBootPath(path = '') {
  return isLightBootPublicPath(normalizePath(path));
}

function injectFbqScript() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (window.fbq) return true;

  // Snippet officiel Meta : file d’attente jusqu’au chargement de fbevents.js
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js'
  );
  return true;
}

function bootstrapFbq() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(false);
  }
  if (window.fbq) return Promise.resolve(true);
  if (fbqBootstrapPromise) return fbqBootstrapPromise;

  fbqBootstrapPromise = new Promise((resolve) => {
    deferMetaPixelWork(() => {
      resolve(injectFbqScript());
    });
  });
  return fbqBootstrapPromise;
}

export function initMetaPixel() {
  return ensureMetaPixelInitialized(null, { allowPublicLightBootPath: true });
}

async function ensureMetaPixelInitialized(path = null, options = {}) {
  if (!ENABLED || !PIXEL_ID) return false;
  if (!options.allowPublicLightBootPath && isPublicLightBootPath(path)) return false;
  await bootstrapFbq();
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return false;
  if (initDone) return true;
  window.fbq('init', PIXEL_ID);
  initDone = true;
  return true;
}

/** PageView SPA — à appeler après chaque navigation (router.afterEach). */
export function trackMetaPixelPageView(path = null) {
  if (!ENABLED || !PIXEL_ID) return false;
  void ensureMetaPixelInitialized(path, { allowPublicLightBootPath: true }).then((ready) => {
    if (!ready) return;
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
    window.fbq('track', 'PageView');
  });
  return true;
}

/** Événements standards Meta Pixel (fbq track). Le reste passe en trackCustom. */
const META_STANDARD_EVENTS = new Set([
  'AddPaymentInfo',
  'AddToCart',
  'AddToWishlist',
  'CompleteRegistration',
  'Contact',
  'CustomizeProduct',
  'Donate',
  'FindLocation',
  'InitiateCheckout',
  'Lead',
  'PageView',
  'Purchase',
  'Schedule',
  'Search',
  'StartTrial',
  'SubmitApplication',
  'Subscribe',
  'ViewContent',
]);

/** Événements standards ou personnalisés Meta (Lead, CompleteRegistration, MagicLinkRequested, etc.). */
export function trackMetaPixel(eventName, params = {}) {
  if (!ENABLED || !PIXEL_ID || !eventName) return false;
  const name = String(eventName).trim();
  if (!name) return false;
  const payload = params && typeof params === 'object' ? params : {};
  void ensureMetaPixelInitialized(null, { allowPublicLightBootPath: true }).then((ready) => {
    if (!ready) return;
    if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
    if (META_STANDARD_EVENTS.has(name)) {
      window.fbq('track', name, payload);
    } else {
      window.fbq('trackCustom', name, payload);
    }
  });
  return true;
}

export function applyMetaPixelConsent(accepted) {
  if (!accepted) {
    queuedPageViewPath = null;
    return false;
  }
  const currentPath =
    queuedPageViewPath ||
    (typeof window !== 'undefined'
      ? window.location.pathname + window.location.search
      : null);
  queuedPageViewPath = null;
  return trackMetaPixelPageView(currentPath);
}
